import { Locales } from '@griply/types';
import {
  AuthError,
  AuthErrorCodes,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { getGlobalAllowedSearchParameters } from 'shared/lib/react-router-dom';
import { getErrorReporter } from 'shared/services/error-tracking';
import { SignupErrorTypes } from 'shared/types/signup-form';

import { createPushToken } from './create-push-token';
import { finishUserSignup } from './finish-user-signup';
import { getPushToken } from './get-push-token';
import { getAuthentication } from './helpers';
import { processOnboardingAnswers } from './process-onboarding-answers';

export const signupUser = async (
  email: string,
  password: string,
  name?: string,
  locale?: Locales,
) => {
  const auth = await getAuthentication();

  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const notificationsEnabled =
      'Notification' in window && Notification.permission === 'granted';

    await finishUserSignup({
      uid: userCredential.user.uid,
      name,
      locale,
      hasNotifications: notificationsEnabled,
      queryParameters: Object.fromEntries(getGlobalAllowedSearchParameters()),
    });

    if (notificationsEnabled) {
      const token = await getPushToken(userCredential.user.uid);
      token && (await createPushToken(userCredential.user.uid, token, locale));
    }

    // best-effort: the account already exists, so failure must not fail the signup
    try {
      await processOnboardingAnswers();
    } catch (onboardingError) {
      getErrorReporter().captureException(onboardingError, {
        tags: { source: 'process-onboarding-answers' },
      });
    }

    return userCredential.user.uid;
  } catch (e) {
    if ((e as AuthError).message?.includes(AuthErrorCodes.EMAIL_EXISTS)) {
      throw SignupErrorTypes.EmailTaken;
    }

    if ((e as AuthError).code === AuthErrorCodes.EMAIL_EXISTS) {
      throw SignupErrorTypes.EmailTaken;
    }

    getErrorReporter().captureException(e, {
      tags: { source: 'firebase-signup' },
    });

    throw SignupErrorTypes.Unknown;
  }
};
